import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import type { ApiResponse, PaginationMeta } from '../types';
import { toErrorMessage } from '../errors/api-error';

/** Extract pagination from response, with a safe fallback for non-paginated lists */
export function extractPagination<T>(res: ApiResponse<T[]>): PaginationMeta {
  if (res.meta.pagination) return res.meta.pagination;
  const items = Array.isArray(res.data) ? res.data.length : 0;
  return { page: 0, pageSize: items, totalElements: items, totalPages: items > 0 ? 1 : 0 };
}

/** Absolute API root, e.g. http://localhost:8081/api/v1. Exported so
 *  non-axios transports (the notifications SSE stream) can reuse it. */
export const BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;

// Tokens are persisted to localStorage so a page reload / new tab / deep-link
// keeps the user signed in. A module-level cache speeds up the request
// interceptor (no localStorage read per request).
const ACCESS_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

let accessToken: string | null = typeof window !== 'undefined'
  ? localStorage.getItem(ACCESS_KEY)
  : null;

export const tokenStore = {
  getAccess: () => accessToken,
  setAccess: (t: string) => {
    accessToken = t;
    localStorage.setItem(ACCESS_KEY, t);
  },
  clearAccess: () => {
    accessToken = null;
    localStorage.removeItem(ACCESS_KEY);
  },
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  setRefresh: (t: string) => localStorage.setItem(REFRESH_KEY, t),
  clearAll: () => {
    accessToken = null;
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const instance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

instance.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Shape returned by {@link refreshSession}. `user` is the up-to-date
 * AuthUser the backend now sends on every /auth/refresh.
 */
export interface RefreshResult {
  accessToken: string;
  refreshToken: string;
  user?: unknown;
}

let refreshPromise: Promise<RefreshResult> | null = null;

/**
 * Single-flight token refresh. Every concurrent caller — the response
 * interceptor below AND the AuthProvider's boot-time rehydration — shares
 * ONE in-flight POST /auth/refresh.
 *
 * This matters because the backend ROTATES the refresh token: it revokes
 * the presented token and issues a new one. Two concurrent refreshes with
 * the same token race — the loser presents an already-revoked token, gets a
 * 401, and spuriously logs the user out right after login. Sharing a single
 * promise guarantees exactly one rotation per refresh cycle.
 *
 * Uses a bare axios call (not `instance`) so the interceptor never sees the
 * refresh request — a 401 here is terminal, not retryable.
 */
export function refreshSession(): Promise<RefreshResult> {
  if (refreshPromise) return refreshPromise;
  const refresh = tokenStore.getRefresh();
  if (!refresh) return Promise.reject(new Error('No refresh token available.'));
  refreshPromise = axios
    .post<{ data: RefreshResult }>(`${BASE_URL}/auth/refresh`, { refreshToken: refresh })
    .then((res) => {
      const { accessToken, refreshToken, user } = res.data.data;
      tokenStore.setAccess(accessToken);
      tokenStore.setRefresh(refreshToken);
      return { accessToken, refreshToken, user };
    })
    .finally(() => {
      // Release the slot so the NEXT 401 (with the freshly rotated token)
      // can start a new cycle.
      refreshPromise = null;
    });
  return refreshPromise;
}

instance.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const orig = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = orig?.url ?? '';
    // A 401 from an auth endpoint itself is terminal — the credentials are
    // invalid, so retrying via refresh would just loop. Fall through to a
    // plain rejection instead.
    const isAuthCall =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/register');

    if (error.response?.status === 401 && orig && !orig._retry && !isAuthCall) {
      orig._retry = true;
      try {
        const { accessToken } = await refreshSession();
        orig.headers = { ...orig.headers, Authorization: `Bearer ${accessToken}` };
        return instance(orig);
      } catch (e) {
        tokenStore.clearAll();
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  },
);

export class ApiClient {
  static async get<T, P extends object = Record<string, unknown>>(
    url: string,
    params?: P,
  ): Promise<ApiResponse<T>> {
    return (await instance.get<ApiResponse<T>>(url, { params })).data;
  }
  static async post<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return (await instance.post<ApiResponse<T>>(url, body)).data;
  }
  static async patch<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return (await instance.patch<ApiResponse<T>>(url, body)).data;
  }
  static async put<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return (await instance.put<ApiResponse<T>>(url, body)).data;
  }
  static async delete<T = void>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    // axios DELETE supports a body only via the `data` config field.
    return (
      await instance.delete<ApiResponse<T>>(
        url,
        body !== undefined ? { data: body } : undefined,
      )
    ).data;
  }
  /** Fetch a binary response (e.g. PDF, PNG) through the authenticated instance. */
  static async getBlob<P extends object = Record<string, unknown>>(
    url: string,
    params?: P,
    accept = 'application/octet-stream',
  ): Promise<Blob> {
    return (
      await instance.get<Blob>(url, {
        params,
        responseType: 'blob',
        headers: { Accept: accept },
      })
    ).data;
  }
  /**
   * Multipart upload with optional upload-progress callback (0-100).
   * Lets axios set the multipart boundary by passing the FormData directly;
   * never hard-code the Content-Type here or the boundary is lost.
   */
  static async postMultipart<T>(
    url: string,
    form: FormData,
    onProgress?: (percent: number) => void,
  ): Promise<ApiResponse<T>> {
    return (
      await instance.post<ApiResponse<T>>(url, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => {
              if (!e.total) return;
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          : undefined,
        timeout: 60_000,
      })
    ).data;
  }
  /** @deprecated Prefer `useApiError().translate(err)` in components, or `toErrorMessage(err, t)` elsewhere. */
  static getErrorMessage(error: unknown): string {
    return toErrorMessage(error);
  }
}
